/**
 * shareMemorySnap — universal share helper for a single Memory Snap.
 *
 * Per product direction the caption stays guest-authored — clean,
 * minimal, screenshot-safe. We bolt on a tiny `Powered by eventdrop.co`
 * attribution line so the brand stays present without overwriting the
 * guest's voice.
 *
 *   Default caption:
 *     "My memory from {event_name} 💛"
 *     "Powered by eventdrop.co"
 *
 * Behaviour:
 *   1) If the browser supports `navigator.share({ files })`, present
 *      the system share sheet with the Polaroid PNG attached.
 *   2) Otherwise copy the caption + URL to the clipboard so the guest
 *      can paste into WhatsApp / Messages / Instagram themselves.
 *
 * Callers receive `{ ok, method }` so the toast can read accurately:
 * "Shared" vs "Caption copied" vs "Couldn't share" — guests must
 * always know what just happened with their memory.
 */

import { polaroidToFile } from './polaroidExport';

const buildCaption = ({ event }) => {
  const name = (event?.event_name || '').trim();
  const head = name ? `My memory from ${name} 💛` : 'My memory 💛';
  return `${head}\n\nPowered by eventdrop.co`;
};

const wallShareUrl = ({ event, snap }) => {
  // Prefer the production origin (eventdrop.co) over the preview host
  // so shared links stay branded — mirrors the platform-wide rule that
  // share URLs use `window.location.origin`, not REACT_APP_BACKEND_URL.
  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
  const slug = event?.slug || snap?.event_slug;
  return slug ? `${origin}/event/${slug}/memory-wall` : origin;
};

export const shareMemorySnap = async ({ snap, event }) => {
  const caption = buildCaption({ event });
  const url = wallShareUrl({ event, snap });
  const shareText = `${caption}\n${url}`;

  // 1) Native file share — preferred everywhere it works (iOS 15+,
  //    Android Chrome, recent Safari/Edge desktop).
  try {
    const file = await polaroidToFile(snap, event);
    const payload = file
      ? { files: [file], text: caption, title: caption.split('\n')[0] }
      : { text: shareText, title: caption.split('\n')[0], url };
    if (typeof navigator !== 'undefined' && navigator.share && (!file || (navigator.canShare && navigator.canShare({ files: [file] })))) {
      try {
        await navigator.share(payload);
        return { ok: true, method: 'native' };
      } catch (err) {
        // User cancelled — treat as a no-op (not an error toast).
        if (err && err.name === 'AbortError') return { ok: true, method: 'cancelled' };
      }
    }
  } catch {
    /* fall through to clipboard */
  }

  // 2) Clipboard fallback — copy the caption + wall URL so the guest
  //    can paste into whatever app they want.
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareText);
      return { ok: true, method: 'clipboard' };
    }
  } catch {
    /* one more fallback */
  }

  // 3) Last resort — open the wall URL so the user can share manually.
  if (typeof window !== 'undefined' && url) {
    window.open(url, '_blank', 'noopener');
    return { ok: true, method: 'opened' };
  }
  return { ok: false };
};

export const memorySnapShareCaption = buildCaption;
